/**
 * Defines an interface intended to be implemented by all layout containers that include mechanisms for
 * maximizing their children.  It provides a means for propagating maximize/restore up the container
 * hierarchy.
 *
 * @module MW/layout/MaximizingContainer
 * @copyright Copyright 2016 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "MW/layout/LayoutUtils"
], function (declare, LayoutUtils) {
    var MaximizingContainer =  declare([], {

        /**
         * Should be overridden to return true if the specified child can be maximized within its immediate container
         * given its current state.  If the child is already maximized within the container this method should return false.
         *
         * @param child {_Widget}
         * @instance
         */
        canMaximizeLocally: function (child) {
            return false;
        },

        /**
         * Should be overridden to return true if the specified child can be restored within its immediate container
         * given its current state.  If the child is not maximized within the container this method should return false.
         *
         * @param child {_Widget}
         * @instance
         */
        canRestoreLocally: function (child) {
            return false;
        },

        /**
         * Should be overridden to maximize the specified child within the container
         *
         * @param child {_Widget}
         * @instance
         */
        maximizeLocally: function (child) {},

        /**
         * Should be overridden to restore (from maximized) the specified child within the container
         *
         * @param child {_Widget}
         * @instance
         */
        restoreLocally: function (child) {}
    });

    /**
     * Returns true if there is any MaximizingContainer within which the given child can be maximized
     *
     * @param child
     * @static
     */
    MaximizingContainer.canMaximizeGlobally = function (child) {
        var ancestor = MaximizingContainer._getMaximizingContainerAncestor(child);
        return (ancestor && (ancestor.canMaximizeLocally(child) ||
                             MaximizingContainer.canMaximizeGlobally(ancestor)));
    };

    /**
     * Returns true if there is any MaximizingContainer within which the given child can be restored
     *
     * @param child
     * @static
     */
    MaximizingContainer.canRestoreGlobally = function (child) {
        var ancestor = MaximizingContainer._getMaximizingContainerAncestor(child);
        return (ancestor && (ancestor.canRestoreLocally(child) ||
                             MaximizingContainer.canRestoreGlobally(ancestor)));
    };

    /**
     * Maximizes the given child and any ancestors that can be maximized
     *
     * @param child
     */
    MaximizingContainer.maximizeGlobally = function (child) {
        var ancestor = MaximizingContainer._getMaximizingContainerAncestor(child);
        if (ancestor) {
            // Note that ancestors are maximized first.  This is necessary because, at least in the case of the
            // DocumentContainer, maximizing a container can lead to its removal from the hierarchy.
            MaximizingContainer.maximizeGlobally(ancestor);
            if (ancestor.canMaximizeLocally(child)) {
                ancestor.maximizeLocally(child);
            }
        }
    };

    /**
     * Restores the given child and any ancestors that can be maximized
     *
     * @param child
     */
    MaximizingContainer.restoreGlobally = function (child) {
        var ancestor = MaximizingContainer._getMaximizingContainerAncestor(child);
        if (ancestor) {
            // Note that ancestors are restored first.  This is necessary because, at least in the case of the
            // DocumentContainer, restoring a container can change the hierarchy below it.
            MaximizingContainer.restoreGlobally(ancestor);
            if (ancestor.canRestoreLocally(child)) {
                ancestor.restoreLocally(child);
            }
        }
    };

    MaximizingContainer._getMaximizingContainerAncestor = function (child) {
        return LayoutUtils.getAncestorOfClass(child, MaximizingContainer);
    };

    return MaximizingContainer;
});